import { ControllerMessageType, WorkerMessageType } from "../shared/Messages.js";
import { isTransferDescriptor } from "../shared/TransferDescriptor.js";
import { assertSharedWorkerScope, assertWorkerScope, isDedicatedWorkerScope, isSharedWorkerScope } from "./Utils.js";
function postModuleInitMessage(context, methodNames) {
    const initMsg = {
        type: WorkerMessageType.Init,
        methodNames: methodNames
    };
    context.postMessage(initMsg);
}
function postUncaughtErrorMessage(context, error) {
    try {
        const errorMessage = {
            type: WorkerMessageType.UnchaughtError,
            errorMessage: error.message
        };
        context.postMessage(errorMessage);
    }
    catch (subError) {
        console.error("Not reporting uncaught error back to master thread as it " +
            "occured while reporting an uncaught error already." +
            "\nLatest error:", subError, "\nOriginal error:", error);
    }
}
function prepareResult(rawResult) {
    if (isTransferDescriptor(rawResult)) {
        return { result: rawResult.send, transferables: rawResult.transferables };
    }
    else {
        return { result: rawResult, transferables: [] };
    }
}
function postWorkerTaskResultMessage(context, taskUid, rawResult) {
    const { result, transferables } = prepareResult(rawResult);
    const taskResultMessage = {
        type: WorkerMessageType.TaskResult,
        uid: taskUid,
        result: result
    };
    context.postMessage(taskResultMessage, transferables);
}
function postWorkerTaskErrorMessage(context, taskUid, error) {
    const taskErrorMessage = {
        type: WorkerMessageType.TaskError,
        uid: taskUid,
        errorMessage: error.message
    };
    context.postMessage(taskErrorMessage);
}
async function runFunction(context, taskUid, fn, args) {
    try {
        let res = fn(...args);
        if (res instanceof Promise)
            res = await res;
        postWorkerTaskResultMessage(context, taskUid, res);
    }
    catch (error) {
        postWorkerTaskErrorMessage(context, taskUid, error);
    }
}
let workerApiExposed = false;
const workerScope = self;
if (isDedicatedWorkerScope(workerScope)) {
    workerScope.addEventListener("error", event => {
        event.preventDefault();
        postUncaughtErrorMessage(workerScope, event.error || event);
    });
    workerScope.addEventListener("unhandledrejection", event => {
        event.preventDefault();
        postUncaughtErrorMessage(workerScope, event.reason || event);
    });
}
export function exposeApi(api) {
    assertWorkerScope(workerScope);
    if (workerApiExposed)
        throw new Error("exposeApi() should only be called once.");
    workerApiExposed = true;
    const exposeApiInner = (context) => {
        if (typeof api === "object" && api) {
            context.onmessage = (event) => {
                const eventContext = event.currentTarget;
                const messageData = event.data;
                switch (messageData.type) {
                    case ControllerMessageType.Run:
                        runFunction(eventContext, messageData.uid, api[messageData.method], messageData.args);
                        break;
                    case ControllerMessageType.Terminate:
                        eventContext.close();
                        if (isSharedWorkerScope(workerScope)
                            && messageData.forceTerminateShared) {
                            workerScope.close();
                        }
                        break;
                }
            };
            if (context instanceof MessagePort)
                context.start();
            const methodNames = Object.keys(api).filter(key => typeof api[key] === "function");
            postModuleInitMessage(context, methodNames);
        }
        else {
            throw new Error(`Invalid argument passed to exposeApi(). Expected an object, got: ${api}`);
        }
    };
    if (isDedicatedWorkerScope(workerScope))
        exposeApiInner(workerScope);
    else {
        assertSharedWorkerScope(workerScope);
        workerScope.onconnect = (event) => {
            const port = event.ports[0];
            workerScope.onerror = event => {
                event.preventDefault();
                postUncaughtErrorMessage(port, event.error || event);
            };
            workerScope.onunhandledrejection = event => {
                event.preventDefault();
                postUncaughtErrorMessage(port, event.reason || event);
            };
            exposeApiInner(port);
        };
    }
}
